import { Injectable } from '@angular/core';
import { NotificationService } from '@improver/core';
import { LoggedUserService } from '@improver/infrastructure/auth-data-layer';
import { EmploymentAccessPermission, PersonnelPermissions } from '@improver/shared/permissions';
import { filter, flattenDeep, flow, intersection, isNil, some } from 'lodash-es';
import { EmploymentAccess } from './../employment-access.model';
import { EmploymentAccessDataFetcherService } from './employment-access-data-fetcher.service';

@Injectable({
  providedIn: 'root',
})
export class EmploymentAccessService {
  constructor(
    private loggedUserService: LoggedUserService,
    private employmentAccessDataFetcherService: EmploymentAccessDataFetcherService,
    private notificationService: NotificationService
  ) {}

  isAccessGranted(employmentId: number, permissions: string[] = [], strict?: boolean): boolean {
    if (this.hasAccessToEmployment(employmentId)) {
      return permissions.length ? this.hasPermission(employmentId, permissions, strict) : true;
    }

    return false;
  }

  isUserAccessGranted(userId: string, permissions: string[] = [], strict?: boolean): boolean {
    if (this.hasAccessToEmployee(userId)) {
      return permissions.length ? this.userHasPermission(userId, permissions, strict) : true;
    }

    return false;
  }

  hasPermission(employmentId: number, permissions: string[], strict?: boolean): boolean {
    return this.hasGivenPermission(
      this.employmentAccessDataFetcherService.getAllEmploymentsAccessData(),
      employmentId,
      null,
      permissions,
      strict
    );
  }

  hasOwnPermission(employmentId: number, companyId: number, permissions: string[]): boolean {
    const myEmployments: EmploymentAccess[] = this.employmentAccessDataFetcherService.getMyEmploymentsAccessData();
    const activeEmployments: EmploymentAccess[] = myEmployments?.filter((item: EmploymentAccess) => item.companyId === companyId);
    const activeEmployment: EmploymentAccess = activeEmployments?.find((item: EmploymentAccess) => item.employmentId === employmentId);

    return activeEmployment
      ? activeEmployments.some((employment: EmploymentAccess) =>
          permissions.some((permission: string) => (employment?.permissions as any)?.includes(permission))
        )
      : false;
  }

  hasEmploymentOwnPermission(employmentId: number, permissions: string[]): boolean {
    const myEmployments: EmploymentAccess[] = this.employmentAccessDataFetcherService.getMyEmploymentsAccessData();
    const activeEmployment: EmploymentAccess = myEmployments?.find((item: EmploymentAccess) => item.employmentId === employmentId);

    return activeEmployment
      ? permissions.some((permission: string) => (activeEmployment?.permissions as any)?.includes(permission))
      : false;
  }

  hasAccessToEmployment(employmentId: number, checkOnlyMine?: boolean): boolean {
    const employments: EmploymentAccess[] = checkOnlyMine
      ? this.employmentAccessDataFetcherService.getMyEmploymentsAccessData()
      : this.employmentAccessDataFetcherService.getAllEmploymentsAccessData();

    return some(employments, (employment: EmploymentAccess) => employment.employmentId === employmentId);
  }

  hasPermissionsToSelectedCompanyEmployeeEmployment(
    permissions: string[],
    ownPermissions: string[],
    employeeId: number,
    requiredAllPermissions?: boolean
  ): boolean {
    const ownEmployments: EmploymentAccess[] = this.employmentAccessDataFetcherService.getMyEmploymentsAccessData();
    const ownEmployment: EmploymentAccess = ownEmployments.find((item: EmploymentAccess) => item.employmentId === employeeId);

    if (ownEmployment) {
      if (requiredAllPermissions) {
        return ownPermissions.some((permission: EmploymentAccessPermission) => (ownEmployment?.permissions as any)?.includes(permission));
      } else {
        return ownPermissions.every((permission: EmploymentAccessPermission) => (ownEmployment?.permissions as any)?.includes(permission));
      }
    }

    const managedEmployments: EmploymentAccess[] = this.employmentAccessDataFetcherService.getManagedEmploymentsAccessData();
    const managedEmployment: EmploymentAccess = managedEmployments.find((item: EmploymentAccess) => item.employmentId === employeeId);

    if (requiredAllPermissions) {
      return permissions.some((permission: EmploymentAccessPermission) => (managedEmployment?.permissions as any)?.includes(permission));
    } else {
      return permissions.every((permission: EmploymentAccessPermission) => (managedEmployment?.permissions as any)?.includes(permission));
    }
  }

  hasAccessToAnyCompanyEmployeeEmployment(userId: string, companyId: number): boolean {
    const ownCompanyEmployee: boolean = this.loggedUserService.isLoggedUser(userId);
    const accessData: EmploymentAccess[] = ownCompanyEmployee
      ? this.employmentAccessDataFetcherService.getMyEmploymentsAccessData()
      : this.employmentAccessDataFetcherService.getManagedEmploymentsAccessData();

    return accessData.some((employmentAccess: EmploymentAccess) => this.isFromCompanyEmployee(employmentAccess, companyId, userId));
  }

  /**
   * @deprecated use hasPermissionsAtLeastOneEmployeeEmployment instead of this method.
   *
   * Here only check own permissions when user is logged but should check in management permission too.
   * Correct implementation is in hasPermissionsAtLeastOneEmployeeEmployment
   */
  hasPermissionsToAnyCompanyEmployeeEmployment(
    permissions: string[],
    ownPermissions: string[],
    companyId: number,
    userId: string,
    requireAllPermissions = false
  ): boolean {
    const ownCompanyEmployee: boolean = this.loggedUserService.isLoggedUser(userId);
    const accessData: EmploymentAccess[] = ownCompanyEmployee
      ? this.employmentAccessDataFetcherService.getMyEmploymentsAccessData()
      : this.employmentAccessDataFetcherService.getManagedEmploymentsAccessData();
    const companyEmployeeEmployments: EmploymentAccess[] = accessData.filter((employmentAccess: EmploymentAccess) =>
      this.isFromCompanyEmployee(employmentAccess, Number(companyId), userId)
    );
    const checkedPermissions: string[] = ownCompanyEmployee ? ownPermissions : permissions;

    if (requireAllPermissions) {
      return companyEmployeeEmployments.some((employmentAccess: EmploymentAccess) =>
        checkedPermissions.every((permission: EmploymentAccessPermission) => employmentAccess.permissions.includes(permission))
      );
    }

    return companyEmployeeEmployments.some((employmentAccess: EmploymentAccess) =>
      checkedPermissions.some((permission: EmploymentAccessPermission) => employmentAccess.permissions.includes(permission))
    );
  }

  /**
   * Checked own permission in my own employments when is logged employee.
   * When own permission doesn't exist check permission in manage employments (advanced access).
   */
  hasPermissionsAtLeastOneEmployeeEmployment(
    permissions: string[],
    ownPermissions: string[],
    companyId: number,
    employeeId: string,
    requireAllPermissions = false
  ): boolean {
    const isLoggedUser: boolean = this.loggedUserService.isLoggedUser(employeeId);

    const myOwnEmploymentsAccess: EmploymentAccess[] =
      this.employmentAccessDataFetcherService.getOwnEmploymentsAccessDataByEmployeeAndCompany(employeeId, companyId);

    const myManageEmploymentsAccess: EmploymentAccess[] =
      this.employmentAccessDataFetcherService.getManagedEmploymentsAccessDataByEmployeeAndCompany(employeeId, companyId);

    let hasManageEmploymentsPermission: boolean = myManageEmploymentsAccess?.some((employmentAccess: EmploymentAccess) =>
      permissions?.some((permission: EmploymentAccessPermission) => employmentAccess.permissions.includes(permission))
    );

    let hasOwnEmploymentsPermission: boolean = myOwnEmploymentsAccess?.some((employmentAccess: EmploymentAccess) =>
      ownPermissions?.some((ownPermission: EmploymentAccessPermission) => employmentAccess.permissions.includes(ownPermission))
    );

    if (requireAllPermissions) {
      hasManageEmploymentsPermission = myManageEmploymentsAccess?.some((employmentAccess: EmploymentAccess) =>
        permissions?.every((permission: EmploymentAccessPermission) => employmentAccess.permissions.includes(permission))
      );

      hasOwnEmploymentsPermission = myOwnEmploymentsAccess?.some((employmentAccess: EmploymentAccess) =>
        ownPermissions?.every((ownPermission: EmploymentAccessPermission) => employmentAccess.permissions.includes(ownPermission))
      );
    }

    if (!isLoggedUser) {
      return hasManageEmploymentsPermission;
    }

    return hasOwnEmploymentsPermission ? true : hasManageEmploymentsPermission;
  }

  canAccessToOnboardingCompanyEmployee(companyId: number, employmentId: number): boolean {
    const vacantUserId = null;
    const hasAccessToAnyEmployment: boolean = this.hasAccessToAnyCompanyEmployeeEmployment(vacantUserId, companyId);

    if (!hasAccessToAnyEmployment) {
      this.notificationService.error();

      return false;
    }

    const hasPermissionToVacant: boolean = this.hasPermission(Number(employmentId), [
      PersonnelPermissions.MANAGE_INST_PERSONNEL_VIEW_ADVANCED,
    ]);

    if (!hasPermissionToVacant) {
      this.notificationService.error();

      return false;
    }

    return true;
  }

  getUserId(): string {
    return this.loggedUserService.getAuthorizedUserDataValue().id;
  }

  private userHasPermission(userId: string, permissions: string[], strict?: boolean): boolean {
    return this.hasGivenPermission(
      this.employmentAccessDataFetcherService.getAllEmploymentsAccessData(),
      null,
      userId,
      permissions,
      strict
    );
  }

  private hasAccessToEmployee(userId: string): boolean {
    return some(
      this.employmentAccessDataFetcherService.getAllEmploymentsAccessData(),
      (employment: EmploymentAccess) => employment.userId === userId
    );
  }

  private isFromCompanyEmployee(employmentAccess: EmploymentAccess, companyId: number, userId: string) {
    return employmentAccess.companyId === companyId && employmentAccess.userId === userId;
  }

  private hasGivenPermission(
    data: EmploymentAccess[],
    employmentId: number,
    userId: string,
    permissions: string[],
    strict?: boolean
  ): boolean {
    const permissionsToCheck: string[] = permissions;
    const permissionsForEmployment: EmploymentAccessPermission[] = flow(
      (item: EmploymentAccess[]) =>
        filter(item, (value: EmploymentAccess) => {
          if (!isNil(employmentId) && isNil(userId)) {
            return value.employmentId === employmentId;
          }

          if (isNil(employmentId) && !isNil(userId)) {
            return value.userId === userId;
          }

          return false;
        }),
      (item: EmploymentAccess[]) => item.map((value: EmploymentAccess) => value.permissions),
      flattenDeep
    )(data);

    const permissionsIntersection: string[] = intersection(permissionsForEmployment, permissionsToCheck);

    return strict ? permissionsIntersection.length === permissionsToCheck.length : !!permissionsIntersection.length;
  }
}
